import React from 'react';
import { useHistory } from 'react-router-dom';
import Auth from 'src/layouts/Auth/Auth';
import { Images } from 'src/Tenants';
import { PATHS } from 'src/common/constants';
import Button from 'src/components/Button';
import EmailChangeIllustration from 'src/components/EmailChangeIllustration/EmailChangeIllustration';
import {
  useConfirmEmailChange,
  UseConfirmEmailChangeReturn,
} from './useConfirmEmailChange';
import './confirm-email-change.scss';

type ConfirmStep = 'confirming' | 'success' | 'error';

interface ConfirmEmailChangeContentProps {
  step: ConfirmStep;
  email: string;
  errorMessage: string;
  onGoToSignIn: () => void;
  onGoToSignInError: () => void;
}

const ConfirmEmailChangeContent = ({
  step,
  email,
  errorMessage,
  onGoToSignIn,
  onGoToSignInError,
}: ConfirmEmailChangeContentProps) => (
  <div className="cec-content">
    {step === 'confirming' && (
      <>
        <div className="cec-confirming-heading">Confirming your email…</div>
        <div className="cec-confirming-subtext">Please wait a moment.</div>
      </>
    )}

    {step === 'success' && (
      <>
        <EmailChangeIllustration variant="success" className="cec-illustration" />
        <div className="cec-heading">Email changed successfully</div>
        <div className="cec-subtext">
          Your email has been updated to <strong>{email}</strong>. Please sign in
          with your new email to continue.
        </div>
        <Button label="Continue to sign in" color="teal" size="small" onClick={onGoToSignIn} />
      </>
    )}

    {step === 'error' && (
      <>
        <EmailChangeIllustration variant="error" className="cec-illustration" />
        <div className="cec-heading">Confirmation failed</div>
        <div className="cec-subtext">{errorMessage}</div>
        <Button label="Go to sign in" color="white" size="small" onClick={onGoToSignInError} />
      </>
    )}
  </div>
);

const ConfirmEmailChange: React.FC = () => {
  const history: any = useHistory();

  const { step, errorMessage, email }: UseConfirmEmailChangeReturn =
    useConfirmEmailChange();

  const handleGoToSignIn = () => {
    const query = new URLSearchParams({ emailChanged: 'true' });

    if (email) {
      query.set('email', email);
    }

    history.push(`${PATHS.AUTH_SIGN_IN}?${query.toString()}`);
  };

  return (
    <Auth
      title="Confirm Email"
      backgroundImage={Images.signInImage}
      content={
        <ConfirmEmailChangeContent
          step={step}
          email={email}
          errorMessage={errorMessage}
          onGoToSignIn={handleGoToSignIn}
          onGoToSignInError={() => history.push(PATHS.AUTH_SIGN_IN)}
        />
      }
    />
  );
};

export default ConfirmEmailChange;
